import React from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCode, FaMobileAlt, FaDraftingCompass, FaRocket, FaShieldAlt, FaChartLine, FaCheck } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useSEO, { SITE_URL } from '../utils/useSEO';
import './ServiceLanding.css';

const serviceData = {
    'web-development-vadodara': {
        title:       'Web Development Company in Vadodara',
        subtitle:    'High-Performance Web Solutions',
        description: 'We build scalable, secure, and lightning-fast websites and web applications tailored to your business needs in Vadodara and beyond.',
        features: [
            { icon: <FaCode />,      title: 'Custom Web Apps',    desc: 'Tailor-made solutions using React, Node.js, and modern tech stacks.' },
            { icon: <FaRocket />,    title: 'Performance First',  desc: 'Optimised for speed, Core Web Vitals, SEO, and user experience.' },
            { icon: <FaShieldAlt />, title: 'Secure & Scalable',  desc: 'Enterprise-grade security and architecture that grows with you.' }
        ],
        whyChoose: [
            { title: 'Local Expertise, Global Standards', desc: 'Based in Vadodara with clients across India, we combine local market understanding with international development practices. Every project follows code-review workflows, automated testing, and CI/CD pipelines.' },
            { title: 'SEO Built Into Architecture', desc: 'We don\'t bolt SEO on after launch. Our React applications ship with server-side rendering, prerendered routes, structured data, and Core Web Vitals optimisation from day one — so your site ranks as soon as it goes live.' },
            { title: 'Transparent Pricing, No Lock-In', desc: 'You own your code, your domain, and your hosting. We provide clear project milestones, weekly progress reports, and fixed-scope pricing so there are no surprises on your invoice.' },
            { title: 'Post-Launch Support Included', desc: 'Every web project includes 90 days of bug-fix support after launch. We also offer ongoing maintenance plans for hosting, security patches, and feature updates.' },
        ],
        processSteps: [
            { step: '01', title: 'Discovery & Planning', desc: 'We analyse your business goals, target audience, and competitor landscape. This phase produces a sitemap, wireframes, and a detailed project timeline.' },
            { step: '02', title: 'UI/UX Design', desc: 'Our designers create high-fidelity mockups in Figma. You review and approve every screen before a single line of code is written.' },
            { step: '03', title: 'Frontend Development', desc: 'We build responsive, accessible interfaces using React with component-driven architecture. Every page is tested across Chrome, Safari, Firefox, and Edge.' },
            { step: '04', title: 'Backend & Integration', desc: 'RESTful APIs, database design, payment gateway integration, and third-party service connections — built on Node.js or Laravel depending on project needs.' },
            { step: '05', title: 'Testing & QA', desc: 'Functional testing, performance audits, security scans, and cross-device testing. We fix every issue before your users see them.' },
            { step: '06', title: 'Launch & Optimise', desc: 'We handle deployment, DNS configuration, SSL setup, and CDN configuration. Post-launch, we monitor Core Web Vitals and fix any performance regressions.' },
        ],
        techStack: [
            { name: 'React', reason: 'Component-driven UI library for fast, interactive frontends with a large ecosystem of tools and community support.' },
            { name: 'Node.js', reason: 'JavaScript runtime for building scalable backend services with non-blocking I/O, ideal for real-time applications.' },
            { name: 'Laravel', reason: 'PHP framework for rapid API development with built-in authentication, queue management, and database migrations.' },
            { name: 'PostgreSQL', reason: 'Enterprise-grade relational database with JSON support, full-text search, and proven reliability at scale.' },
            { name: 'AWS / LiteSpeed', reason: 'Cloud infrastructure for auto-scaling, global CDN delivery, and 99.9% uptime SLAs.' },
        ],
        faqs: [
            { q: 'How long does it take to build a website in Vadodara?', a: 'A standard business website takes 4 to 6 weeks from kickoff to launch. Complex web applications with custom dashboards or integrations typically take 8 to 12 weeks. We provide a detailed timeline during the discovery phase.' },
            { q: 'Do you build WordPress websites or only custom sites?', a: 'We specialise in custom-coded websites using React and Node.js because they deliver better performance, security, and scalability. For content-heavy sites where WordPress makes sense, we can build custom themes — but we recommend headless CMS setups for the best of both worlds.' },
            { q: 'Will my website be mobile-friendly?', a: 'Every website we build is responsive by default. We design mobile-first and test on real devices across iOS and Android. Our sites consistently score above 90 on Google\'s mobile usability tests.' },
            { q: 'Can you redesign my existing website?', a: 'Yes. We regularly handle website redesigns for businesses in Vadodara and across Gujarat. We audit your current site, preserve your SEO equity during migration, and rebuild with modern technology for better speed and conversions.' },
        ],
        relatedLinks: [
            { label: 'View Our Pricing Plans', path: '/pricing' },
            { label: 'See Our Portfolio', path: '/portfolio' },
            { label: 'Read: How to Choose a Web Development Company in Vadodara', path: '/blog/how-to-choose-right-web-development-company-vadodara' },
            { label: 'Read: Core Web Vitals & Google Ranking Explained', path: '/blog/core-web-vitals-google-ranking-explained' },
        ],
        metaTitle:       'Web Development Company in Vadodara | Nimai Solutions',
        metaDescription: 'Looking for a web development company in Vadodara? Nimai Solutions builds fast, secure, SEO-optimised websites and React web applications for businesses across Gujarat.',
        keywords:        'web development company Vadodara, website development Baroda, React developer Gujarat, custom website Vadodara, web design company Gujarat',
        serviceType:     'Web Development',
    },
    'flutter-app-development': {
        title:       'Flutter App Development Services',
        subtitle:    'Cross-Platform Excellence',
        description: 'Leverage the power of Flutter to build beautiful, natively compiled applications for mobile, web, and desktop from a single codebase.',
        features: [
            { icon: <FaMobileAlt />,  title: 'Single Codebase',      desc: 'Reach iOS and Android users simultaneously with high-quality apps.' },
            { icon: <FaRocket />,     title: 'Fast Development',      desc: 'Quick turnaround times with Hot Reload and pre-built widgets.' },
            { icon: <FaChartLine />,  title: 'Native Performance',    desc: 'Smooth animations and high performance that feels truly native.' }
        ],
        whyChoose: [
            { title: 'One Codebase, Two App Stores', desc: 'Flutter compiles to native ARM code for both iOS and Android. You get two fully native apps from a single development effort — cutting your budget by 30 to 40 percent compared to building separate native apps.' },
            { title: 'Pixel-Perfect Custom UI', desc: 'Flutter\'s rendering engine draws every pixel on screen, which means we are not limited by platform-specific UI components. Your app looks exactly the same on a Pixel phone as it does on an iPhone — or we can customise it per platform.' },
            { title: 'Faster Time to Market', desc: 'Hot Reload lets us see changes instantly during development. Combined with Flutter\'s rich widget library, we deliver production-ready apps in 6 to 10 weeks — significantly faster than traditional native development.' },
            { title: 'Google-Backed, Future-Proof', desc: 'Flutter is Google\'s primary UI toolkit with over 160,000 stars on GitHub and backing from companies like BMW, Alibaba, and ByteDance. Your investment is protected by one of the most active open-source communities in mobile development.' },
        ],
        processSteps: [
            { step: '01', title: 'Requirements & Prototyping', desc: 'We map out your app\'s user flows, define the feature set, and create interactive prototypes in Figma that you can tap through on your own phone before development begins.' },
            { step: '02', title: 'Architecture Design', desc: 'We choose the right state management approach (Riverpod, BLoC, or Provider), define the folder structure, and set up CI/CD pipelines for automated builds and testing.' },
            { step: '03', title: 'Sprint-Based Development', desc: 'We build in two-week sprints, delivering working features you can test on your device at the end of each sprint. You see progress every fortnight, not just at launch.' },
            { step: '04', title: 'API & Backend Integration', desc: 'REST or GraphQL APIs, Firebase services, push notifications, payment gateways, and third-party SDKs — all integrated and tested for both platforms simultaneously.' },
            { step: '05', title: 'Testing & Optimisation', desc: 'Unit tests, widget tests, and integration tests automated in our CI pipeline. We also perform manual testing on 10+ real devices and optimise app size, startup time, and memory usage.' },
            { step: '06', title: 'Store Submission & Launch', desc: 'We handle App Store and Play Store submissions including screenshots, descriptions, and metadata optimisation. Post-launch, we monitor crash reports and user feedback for rapid iteration.' },
        ],
        techStack: [
            { name: 'Flutter / Dart', reason: 'Google\'s UI toolkit for building natively compiled applications for mobile, web, and desktop from a single codebase.' },
            { name: 'Firebase', reason: 'Backend-as-a-service for authentication, real-time database, cloud functions, analytics, and push notifications.' },
            { name: 'Riverpod / BLoC', reason: 'State management solutions that keep your app\'s logic testable, maintainable, and scalable as features grow.' },
            { name: 'REST / GraphQL APIs', reason: 'Flexible backend communication depending on your data requirements and existing infrastructure.' },
            { name: 'Fastlane / Codemagic', reason: 'CI/CD automation for building, testing, and deploying to both app stores with a single command.' },
        ],
        faqs: [
            { q: 'Is Flutter good enough for production apps?', a: 'Flutter powers apps used by hundreds of millions of users globally, including Google Pay, BMW, and Alibaba. At Nimai Solutions, every Flutter app we ship passes the same performance benchmarks we set for native apps — 60fps animations, under 2-second cold starts, and sub-100MB app sizes.' },
            { q: 'How much does a Flutter app cost in India?', a: 'A Flutter MVP with 5 to 8 screens, user authentication, and API integration typically costs between 2 to 4 lakh INR. Full-featured apps with custom backends, admin panels, and payment integration range from 5 to 12 lakh INR. We provide fixed-scope quotes after the discovery phase.' },
            { q: 'Can you convert my existing app to Flutter?', a: 'Yes. We regularly migrate native iOS or Android apps to Flutter. We audit your existing codebase, plan the migration in phases, and ensure feature parity before decommissioning the old apps. Most migrations take 8 to 14 weeks.' },
            { q: 'Does Flutter support web and desktop apps too?', a: 'Flutter supports web, Windows, macOS, and Linux targets from the same codebase. For most of our clients, we start with mobile and add web or desktop when the business case is clear — the shared codebase makes adding platforms incremental, not a rewrite.' },
        ],
        relatedLinks: [
            { label: 'View Our Pricing Plans', path: '/pricing' },
            { label: 'See Our Portfolio', path: '/portfolio' },
            { label: 'Read: Flutter vs React Native in 2025', path: '/blog/flutter-vs-react-native-2025' },
            { label: 'Read: How We Built a Real Estate Platform with Laravel & React', path: '/blog/how-we-built-real-estate-platform-laravel-react' },
        ],
        metaTitle:       'Flutter App Development Services India | Nimai Solutions',
        metaDescription: 'Expert Flutter app development services in India. Build beautiful, high-performance cross-platform mobile apps for iOS and Android from a single codebase.',
        keywords:        'Flutter app development India, Flutter developer Vadodara, cross-platform app development Gujarat, Flutter iOS Android app',
        serviceType:     'Flutter App Development',
    },
    'mobile-app-development': {
        title:       'Mobile App Development Company',
        subtitle:    'Innovative Mobile Solutions',
        description: 'From concept to deployment, we create engaging mobile experiences that connect your brand with users on their favourite devices.',
        features: [
            { icon: <FaMobileAlt />,     title: 'iOS & Android',       desc: 'Deep expertise in both major platforms for maximum reach.' },
            { icon: <FaDraftingCompass />, title: 'User-Centric UI',   desc: 'Interfaces designed for intuitive navigation and high engagement.' },
            { icon: <FaRocket />,         title: 'App Store Success',  desc: 'Complete support for App Store and Play Store optimisation.' }
        ],
        whyChoose: [
            { title: 'Platform-Specific Expertise', desc: 'Whether you need a native Swift app for iOS, a Kotlin app for Android, or a cross-platform Flutter solution, our team has delivered 50+ mobile apps across all three approaches. We recommend the right technology based on your budget, timeline, and feature requirements.' },
            { title: 'Designed for Retention', desc: 'Beautiful download numbers mean nothing if users uninstall after a week. We build onboarding flows, push notification strategies, and engagement loops that keep users coming back. Our apps average 40% higher 30-day retention than industry benchmarks.' },
            { title: 'Offline-First Architecture', desc: 'Many of our clients serve users in areas with inconsistent network coverage across Gujarat and rural India. We architect apps that work offline, sync data when connectivity returns, and never lose user input — critical for field service, logistics, and healthcare apps.' },
            { title: 'End-to-End Ownership', desc: 'From the first wireframe to the App Store screenshot, we handle everything in-house. No subcontracting to overseas teams, no communication gaps. You work directly with the developers and designers building your app.' },
        ],
        processSteps: [
            { step: '01', title: 'Market & User Research', desc: 'We study your target users, analyse competitor apps, and identify feature gaps in the market. This produces a product requirements document and a prioritised feature roadmap.' },
            { step: '02', title: 'UX Strategy & Wireframing', desc: 'Low-fidelity wireframes map every user flow in your app. We validate navigation patterns and information architecture before investing in visual design.' },
            { step: '03', title: 'Visual Design & Prototyping', desc: 'High-fidelity designs in Figma with interactive prototypes. You can install the prototype on your phone and tap through the full experience before development starts.' },
            { step: '04', title: 'Agile Development', desc: 'Two-week sprints with testable builds delivered at the end of each cycle. We use Git-based version control and code reviews on every pull request to maintain quality.' },
            { step: '05', title: 'QA & Device Testing', desc: 'We test on 15+ physical devices covering different screen sizes, OS versions, and manufacturers. Performance profiling ensures smooth scrolling, fast load times, and efficient battery usage.' },
            { step: '06', title: 'Launch & Growth Support', desc: 'App Store Optimisation for visibility, analytics integration for tracking KPIs, and a 90-day post-launch support period for bug fixes and feature iterations based on real user feedback.' },
        ],
        techStack: [
            { name: 'Flutter', reason: 'Cross-platform framework for shipping to iOS and Android from a single codebase with native-level performance.' },
            { name: 'Swift / Kotlin', reason: 'Platform-native languages for apps requiring deep OS integration, ARKit, HealthKit, or platform-specific APIs.' },
            { name: 'Firebase / Supabase', reason: 'Real-time databases, authentication, cloud storage, and serverless functions for rapid backend development.' },
            { name: 'Node.js / Laravel', reason: 'Custom backend APIs for complex business logic, multi-tenant systems, and enterprise integrations.' },
            { name: 'Redux / Riverpod', reason: 'Predictable state management for maintaining app consistency across complex user workflows.' },
        ],
        faqs: [
            { q: 'Should I build a native app or a cross-platform app?', a: 'For most business apps, cross-platform (Flutter) delivers 95% of native performance at 60% of the cost. We recommend native development only when your app requires deep platform integration like ARKit, advanced Bluetooth, or custom camera pipelines. We help you make this decision during the discovery phase.' },
            { q: 'How much does mobile app development cost in Vadodara?', a: 'A basic MVP app with user authentication, a feed, and push notifications typically costs 2 to 5 lakh INR. Feature-rich apps with payment integration, admin panels, real-time chat, and analytics range from 5 to 15 lakh INR. We provide detailed, fixed-scope quotes.' },
            { q: 'How do you handle app maintenance after launch?', a: 'We offer monthly maintenance plans starting from 15,000 INR per month covering OS compatibility updates, bug fixes, minor feature additions, and server monitoring. Every project includes 90 days of free post-launch bug-fix support.' },
            { q: 'Can you build apps that work without internet?', a: 'Yes. We specialise in offline-first mobile architectures using local databases (Hive, SQLite) with background sync. This is especially important for field service apps, healthcare apps, and logistics solutions used in areas with spotty connectivity.' },
        ],
        relatedLinks: [
            { label: 'View Our Pricing Plans', path: '/pricing' },
            { label: 'See Our Portfolio', path: '/portfolio' },
            { label: 'Read: Flutter vs React Native in 2025', path: '/blog/flutter-vs-react-native-2025' },
            { label: 'Read: Why Small Businesses in Gujarat Need a Professional Website', path: '/blog/why-small-business-gujarat-needs-professional-website-2025' },
        ],
        metaTitle:       'Mobile App Development Company Vadodara | iOS & Android | Nimai Solutions',
        metaDescription: 'Professional mobile app development company in Vadodara. We build custom iOS and Android applications that drive business growth and delight users.',
        keywords:        'mobile app development Vadodara, iOS app developer Gujarat, Android app development India, app development company Vadodara',
        serviceType:     'Mobile App Development',
    },
    'ui-ux-design-services': {
        title:       'UI/UX Design Services in Vadodara',
        subtitle:    'Human-Centric Design',
        description: 'We create visually stunning and highly functional designs that prioritise user experience and deliver measurable business outcomes.',
        features: [
            { icon: <FaDraftingCompass />, title: 'Interface Design',     desc: 'Modern, clean, and brand-aligned visual interfaces.' },
            { icon: <FaChartLine />,       title: 'Experience Strategy',  desc: 'Data-driven design decisions based on user behaviour and goals.' },
            { icon: <FaRocket />,          title: 'Prototyping',          desc: 'Interactive prototypes to visualise and test the flow before development.' }
        ],
        whyChoose: [
            { title: 'Design That Converts', desc: 'A beautiful interface that nobody can use is a failed design. We measure success by conversion rates, task completion times, and user satisfaction scores — not just how many Dribbble likes a mockup gets. Our redesigns have increased client conversion rates by 25 to 60 percent.' },
            { title: 'Research-Backed Decisions', desc: 'Every design choice we make is grounded in user research, not personal preference. We conduct user interviews, run card-sorting exercises, and analyse heatmaps and session recordings to understand how real people interact with your product.' },
            { title: 'Accessible by Default', desc: 'We follow WCAG 2.1 AA guidelines in every project. Accessible design is not an add-on — it is baked into our process from colour contrast ratios to keyboard navigation to screen reader compatibility. Accessible products reach more users and rank better in search.' },
            { title: 'Developer-Ready Deliverables', desc: 'Our Figma files ship with auto-layout, design tokens, component variants, and responsive breakpoints. Developers can inspect spacing, export assets, and copy CSS values directly — no guesswork, no back-and-forth.' },
        ],
        processSteps: [
            { step: '01', title: 'Discovery & Audit', desc: 'We review your existing product (or competitor products), interview stakeholders and end users, and document pain points. The output is a UX audit report with prioritised improvement opportunities.' },
            { step: '02', title: 'Information Architecture', desc: 'Sitemaps, user flow diagrams, and content hierarchy mapping. We define how information is organised before deciding how it looks — because structure drives usability.' },
            { step: '03', title: 'Wireframing', desc: 'Low-fidelity wireframes for every key screen and user flow. These are fast to iterate and ensure we solve navigation and layout problems before committing to visual design.' },
            { step: '04', title: 'Visual Design System', desc: 'We create a design system with typography scales, colour palettes, component libraries, and spacing tokens. This system ensures visual consistency across every screen and speeds up future design work.' },
            { step: '05', title: 'Interactive Prototyping', desc: 'Clickable prototypes in Figma that simulate the real app experience. Stakeholders and test users interact with the prototype on their own devices, and we iterate based on their feedback.' },
            { step: '06', title: 'Handoff & QA', desc: 'Design files with developer annotations, asset exports in all required formats, and a design QA phase during development where we review the built product against the approved designs.' },
        ],
        techStack: [
            { name: 'Figma', reason: 'Collaborative design tool for creating, prototyping, and handing off designs to development teams in real-time.' },
            { name: 'FigJam / Miro', reason: 'Whiteboarding tools for workshops, user journey mapping, affinity diagrams, and collaborative ideation sessions.' },
            { name: 'Hotjar / Clarity', reason: 'Heatmap and session recording tools for understanding how real users interact with existing products.' },
            { name: 'Maze / UsabilityHub', reason: 'Remote usability testing platforms for validating designs with real users before development begins.' },
            { name: 'Storybook', reason: 'Component documentation for maintaining design system consistency between design files and production code.' },
        ],
        faqs: [
            { q: 'What is the difference between UI design and UX design?', a: 'UX (User Experience) design focuses on how a product works — the user flows, information architecture, and interaction patterns that make a product intuitive. UI (User Interface) design focuses on how a product looks — typography, colours, spacing, and visual hierarchy. We handle both because a product needs both to succeed.' },
            { q: 'How much do UI/UX design services cost?', a: 'A UX audit of an existing product starts from 50,000 INR. Full UI/UX design for a mobile app (8 to 15 screens) typically costs 1.5 to 3 lakh INR. Website redesigns range from 1 to 4 lakh INR depending on the number of page templates and complexity.' },
            { q: 'Do you conduct user research?', a: 'Yes, user research is a core part of our process. Depending on the project, we conduct user interviews, surveys, card sorting, tree testing, and usability testing. For existing products, we also analyse analytics data and session recordings to identify friction points.' },
            { q: 'Can you redesign our existing app without rebuilding it?', a: 'Yes. We can deliver a complete design system and updated screen designs that your existing development team implements. We also offer design QA services where we review the implementation against our designs to ensure pixel-level accuracy.' },
        ],
        relatedLinks: [
            { label: 'View Our Pricing Plans', path: '/pricing' },
            { label: 'See Our Portfolio', path: '/portfolio' },
            { label: 'Read: UI/UX Design Mistakes Killing Your Conversion Rate', path: '/blog/ui-ux-design-mistakes-killing-conversion-rate' },
            { label: 'Learn About Our Development Process', path: '/process' },
        ],
        metaTitle:       'UI/UX Design Services Vadodara | User Experience Design | Nimai Solutions',
        metaDescription: 'Premium UI/UX design services in Vadodara, Gujarat. We create intuitive, accessible, and engaging digital experiences that users love and businesses trust.',
        keywords:        'UI UX design services Vadodara, user experience design Gujarat, app UI design India, website UX designer Vadodara',
        serviceType:     'UI/UX Design',
    },
};

const buildServiceSchema = (serviceId, data) => ({
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "Service",
            "@id": `${SITE_URL}/${serviceId}/#service`,
            "name": data.title,
            "serviceType": data.serviceType,
            "description": data.description,
            "provider": {
                "@type": "Organization",
                "@id": `${SITE_URL}/#business`,
                "name": "Nimai Solutions",
                "url": `${SITE_URL}/`,
            },
            "areaServed": [
                { "@type": "City",    "name": "Vadodara" },
                { "@type": "State",   "name": "Gujarat" },
                { "@type": "Country", "name": "India" }
            ],
            "url": `${SITE_URL}/${serviceId}/`,
        },
        {
            "@type": "WebPage",
            "@id": `${SITE_URL}/${serviceId}/#webpage`,
            "url": `${SITE_URL}/${serviceId}/`,
            "name": data.metaTitle,
            "description": data.metaDescription,
            "isPartOf": { "@id": `${SITE_URL}/#website` },
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home",          "item": `${SITE_URL}/` },
                    { "@type": "ListItem", "position": 2, "name": data.serviceType, "item": `${SITE_URL}/${serviceId}/` }
                ]
            }
        },
        ...(data.faqs ? [{
            "@type": "FAQPage",
            "mainEntity": data.faqs.map(faq => ({
                "@type": "Question",
                "name": faq.q,
                "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
        }] : [])
    ]
});

const ServiceLanding = () => {
    const { serviceId } = useParams();
    const navigate      = useNavigate();
    const data          = serviceData[serviceId];

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useSEO({
        title:     data?.metaTitle || 'Nimai Solutions',
        description: data?.metaDescription || '',
        canonical: `${SITE_URL}/${serviceId}`,
        keywords:  data?.keywords || '',
        schema:    data ? buildServiceSchema(serviceId, data) : undefined,
    });

    if (!data) {
        return <Navigate to="/404" replace />;
    }

    return (
        <div className="service-landing-container">
            <Navbar />

            <section className="service-landing-hero">
                <motion.span
                    className="service-badge"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {data.subtitle}
                </motion.span>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    {data.title.split(' ').map((word, i) => (
                        <span key={i} className={i >= data.title.split(' ').length - 2 ? 'highlight' : ''}>
                            {word}{' '}
                        </span>
                    ))}
                </motion.h1>
                <motion.p
                    className="service-landing-desc"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    {data.description}
                </motion.p>
                <motion.button
                    className="btn-primary"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    onClick={() => navigate('/contact')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Get a Free Consultation
                </motion.button>
            </section>

            <section className="service-features-section">
                <div className="features-grid">
                    {data.features.map((feature, index) => (
                        <motion.div
                            key={index}
                            className="feature-card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                            <div className="feature-icon">{feature.icon}</div>
                            <h3>{feature.title}</h3>
                            <p>{feature.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {data.whyChoose && (
                <section className="service-why-section">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        Why Choose Nimai Solutions for {data.serviceType}
                    </motion.h2>
                    <div className="why-grid">
                        {data.whyChoose.map((item, index) => (
                            <motion.div
                                key={index}
                                className="why-card"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <div className="why-check"><FaCheck /></div>
                                <div>
                                    <h3>{item.title}</h3>
                                    <p>{item.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {data.processSteps && (
                <section className="service-process-section">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        Our {data.serviceType} Process
                    </motion.h2>
                    <div className="process-grid">
                        {data.processSteps.map((item, index) => (
                            <motion.div
                                key={index}
                                className="process-card"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.08 }}
                            >
                                <span className="process-step-number">{item.step}</span>
                                <h3>{item.title}</h3>
                                <p>{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {data.techStack && (
                <section className="service-tech-section">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        Technologies We Use
                    </motion.h2>
                    <div className="tech-list">
                        {data.techStack.map((tech, index) => (
                            <motion.div
                                key={index}
                                className="tech-item"
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.08 }}
                            >
                                <h4>{tech.name}</h4>
                                <p>{tech.reason}</p>
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {data.faqs && (
                <section className="service-faq-section">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        Frequently Asked Questions
                    </motion.h2>
                    <div className="faq-list">
                        {data.faqs.map((faq, index) => (
                            <motion.div
                                key={index}
                                className="faq-item"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.08 }}
                            >
                                <h3>{faq.q}</h3>
                                <p>{faq.a}</p>
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {data.relatedLinks && (
                <section className="service-links-section">
                    <h3>Explore More</h3>
                    <div className="related-links">
                        {data.relatedLinks.map((link, index) => (
                            <Link key={index} to={link.path} className="related-link">
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            <section className="service-cta">
                <div className="cta-box">
                    <h2>Ready to Scale Your Business?</h2>
                    <p style={{ color: '#A0AEC0', marginBottom: '30px' }}>
                        Let's build something amazing together. Our team is ready to help you achieve your digital goals.
                    </p>
                    <button className="btn-primary" onClick={() => navigate('/contact')}>
                        Contact Us Today
                    </button>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ServiceLanding;
